// UserScreen.js

import axios from "axios";
import React, { useState, useEffect } from "react";
import { API_IP } from "../env";
import PatientsForm from "./Form/PatientsForm";

// Import react-bootstrap components
import Dropdown from "react-bootstrap/Dropdown";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import InputGroup from "react-bootstrap/InputGroup";
import "./UserScreen.css";

function UserScreen() {
  const [userData, setUserData] = useState([]);

  // State for the modal
  const [showPatientForm, setShowPatientForm] = useState(false);
  const [selectedAccountID, setSelectedAccountID] = useState();
  const [selectedUsername, setSelectedUsername] = useState();

  const [refreshUser, setRefreshUser] = useState(false);
  const [loading, setLoading] = useState(true);

  // --- NEW: Pagination State ---
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 1,
  });

  // --- NEW: Search State (with debounce) ---
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  async function axiosReqConfig(url, method, data) {
    const bearerToken = localStorage.getItem("@uphealth_login_token");
    const reqConfig = {
      url: url,
      method: method,
      headers: { Authorization: `Bearer ${bearerToken}` },
      data: data,
    };
    return await axios(reqConfig);
  }

  // --- MODIFIED: getAllUsers to support pagination and search ---
  async function getAllUsers(page = 1, search = "") {
    setLoading(true);
    try {
      // Pass page, limit, and search to the API
      const { data } = await axiosReqConfig(
        `${API_IP}/api/admin/users?page=${page}&limit=10&search=${encodeURIComponent(
          search
        )}`,
        "get",
        []
      );

      // Handle new paginated response
      if (data && data.pagination) {
        setUserData(data.data);
        setPaginationInfo(data.pagination);
      } else {
        // Fallback
        setUserData(data);
      }
    } catch (err) {
      console.error("Failed to fetch users", err);
    }
    setLoading(false);
  }

  // --- NEW: Debounce effect for search ---
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // --- MODIFIED: Main data fetch effect ---
  useEffect(() => {
    getAllUsers(1, debouncedSearchTerm); // Fetch page 1 when search term changes
  }, [refreshUser, debouncedSearchTerm]);

  // --- Modal Handlers (unchanged) ---
  const handleOpenPatients = (item) => {
    setSelectedAccountID(item.account_id);
    setSelectedUsername(item.username);
    setShowPatientForm(true);
  };
  const handleClosePatients = () => {
    setShowPatientForm(false);
  };

  // --- NEW: Pagination Handlers ---
  const handleNextPage = () => {
    if (paginationInfo.currentPage < paginationInfo.totalPages) {
      getAllUsers(paginationInfo.currentPage + 1, debouncedSearchTerm);
    }
  };

  const handlePreviousPage = () => {
    if (paginationInfo.currentPage > 1) {
      getAllUsers(paginationInfo.currentPage - 1, debouncedSearchTerm);
    }
  };

  // --- Item Sub-Component (MODIFIED) ---
  function Item({ item }) {
    async function updateAccountStatus(username, account_id, input) {
      let inputStatus;
      let statusText = "";
      if (input === 0) {
        inputStatus = true;
        statusText = "VERIFY";
      } else if (input === 1) {
        inputStatus = false;
        statusText = "UNVERIFY";
      } else {
        inputStatus = null;
        statusText = "DEACTIVATE";
      }

      if (
        window.confirm(`Are you sure you want to ${statusText} ${username}?`)
      ) {
        await axiosReqConfig(
          `${API_IP}/api/admin/users/${account_id}`,
          "patch",
          {
            updateSection: 0,
            updateInfo: inputStatus,
          }
        );
        setRefreshUser(!refreshUser);
      }
    }

    const getStatusVariant = () => {
      if (item.verification === true)
        return { text: "Verified", variant: "success" };
      if (item.verification === false)
        return { text: "Unverified", variant: "warning" };
      return { text: "Deactivated", variant: "danger" };
    };

    const status = getStatusVariant();

    // The return statement was missing all this content
    return (
      <Col md={6} lg={4} className="mb-4">
        <Card className="h-100 user-card">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <Card.Title as="h5" className="mb-0">
              {item.username}
            </Card.Title>
            <Dropdown>
              <Dropdown.Toggle
                variant={status.variant}
                id={`dropdown-${item.account_id}`}
                size="sm"
              >
                {status.text}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={() => {
                    updateAccountStatus(item.username, item.account_id, 0);
                  }}
                >
                  Set as Verified
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    updateAccountStatus(item.username, item.account_id, 1);
                  }}
                >
                  Set as Unverified
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    updateAccountStatus(item.username, item.account_id, 2);
                  }}
                >
                  Set as Deactivated
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Card.Header>
          <Card.Body>
            <div>
              <strong className="small text-muted">Account ID</strong>
              <p className="small text-dark fw-bold">{item.account_id}</p>
            </div>
            <div>
              <strong className="small text-muted">Email</strong>
              <p className="text-dark fw-bold">{item.email}</p>
            </div>
            <div>
              <strong className="small text-muted">Phone</strong>
              <p className="text-dark fw-bold">{item.phone_number || "N/A"}</p>
            </div>
          </Card.Body>
          <Card.Footer>
            <Button
              variant="primary"
              className="w-100"
              onClick={() => handleOpenPatients(item)}
            >
              <i className="bi bi-people me-2"></i>View Patients
            </Button>
          </Card.Footer>
        </Card>
      </Col>
    );
  }
  // --- END MODIFICATION ---

  return (
    <>
      <Container fluid className="user-management-container px-0">
        {/* Header */}
        <Row className="mb-4 align-items-center card flex-row mx-0 p-4">
          <Col md={8}>
            <p className="text-muted mb-0 fw-semibold">
              {loading
                ? "Loading users..."
                : `Showing ${userData.length} of ${paginationInfo.totalItems} users`}
            </p>
          </Col>
          <Col md={4} className="mt-3 mt-md-0 px-0">
            <InputGroup>
              <InputGroup.Text className="border-0 bg-transparent ps-3">
                <i className="bi bi-search"></i>
              </InputGroup.Text>
              <Form.Control
                type="text"
                className="border-0 bg-transparent py-2"
                placeholder="Search by username, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ boxShadow: "none" }}
              />
            </InputGroup>
          </Col>
        </Row>

        {/* Card Grid */}
        <Row>
          {loading ? (
            <Col className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading users...</span>
              </div>
            </Col>
          ) : (
            // Map over userData directly
            userData.map((item, index) => (
              <Item key={item.account_id || index} item={item} />
            ))
          )}
          {!loading && userData.length === 0 && (
            <Col className="text-center py-5">
              <h4>No users found</h4>
              <p className="text-muted">Try adjusting your search term.</p>
            </Col>
          )}
        </Row>

        {/* --- NEW: Pagination Controls --- */}
        <nav className="mt-4 d-flex justify-content-between align-items-center">
          <Button
            variant="outline-secondary"
            onClick={handlePreviousPage}
            disabled={paginationInfo.currentPage <= 1 || loading}
          >
            &larr; Previous
          </Button>
          <span className="text-muted">
            Page {paginationInfo.currentPage} of {paginationInfo.totalPages}
          </span>
          <Button
            variant="outline-secondary"
            onClick={handleNextPage}
            disabled={
              paginationInfo.currentPage >= paginationInfo.totalPages || loading
            }
          >
            Next &rarr;
          </Button>
        </nav>
      </Container>

      {/* Modal (unchanged) */}
      <PatientsForm
        show={showPatientForm}
        onHide={handleClosePatients}
        accountID={selectedAccountID}
        username={selectedUsername}
      />
    </>
  );
}

export default UserScreen;
